import { index, integer, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// SOURCE DOCUMENTS — the FILE-DOOR lane (tonight's brief: "the PDF needs to be
// stored on a media blob and read... before it can be used"). One row per
// uploaded résumé/CV file (PDF or DOCX): the R2 pointer to the ORIGINAL blob
// (never discarded — nothing-unrecoverable) plus an honest record of what got
// extracted from it. This table is provenance, not corpus content — the
// roles/achievements the extraction yields still land in the SAME roles/
// achievements tables the paste-import path writes, via the SAME reused
// import-core function (corpus.routes.ts). A created row's id is what those
// achievement/role writes stamp into their `source_ref` jsonb (mirrors the
// sourceRef provenance-pointer pattern _shared.ts's substrate documents and
// achievement_versions.sourceRef already uses — traced, never generated).
//
// NEW table → takes the CURRENT-LAW shape from day one (CLAUDE.md's corrected
// 2026-06-22 note: "identity = per-user users.id uuid, text-keyed user_id
// tables are migration debt"), mirroring drive_export.ts's userDriveGrants —
// both sides uuid, a real FK, ON DELETE CASCADE so a deleted user's uploaded
// blobs' provenance rows go with them (the R2 objects themselves are cleaned
// up separately; this table is metadata, not the blob).
//
// extractedChars is nullable: NO INVENTION forbids a fabricated character
// count for a row whose extraction failed before it could count anything
// (the 422 no_text_layer path still creates this row — the blob really was
// stored — but never fakes a character count for text that was never read).
// =============================================================================

export const sourceDocuments = pgTable(
  'source_documents',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    r2Key: text('r2_key').notNull(),
    filename: text('filename'),
    mime: varchar('mime', { length: 64 }),
    sizeBytes: integer('size_bytes'),
    /** Characters of text actually extracted. NULL when extraction produced
     *  nothing usable (e.g. a scanned/image-only PDF) — never a guessed or
     *  zero-as-a-stand-in value; NULL is the only honest state for "we don't
     *  know / there was nothing to count". */
    extractedChars: integer('extracted_chars'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    userIdx: index('source_documents_user_idx').on(t.userId),
  }),
);
